import { ReactNode } from "react";
import { Link, NavLink } from "react-router-dom";
import ReactMarkdown, { type Components } from "react-markdown";
import remarkGfm from "remark-gfm";
import { ArrowLeft } from "lucide-react";
import WeKitLogo from "@/components/WeKitLogo";
import SharedFooter from "@/components/SharedFooter";
import { openCookiePreferences } from "@/components/CookieConsent";
import PolicyAgree from "@/components/PolicyAgree";

export const POLICIES = [
  { to: "/privacy", label: "Privacy Policy" },
  { to: "/terms", label: "Terms & Conditions" },
  { to: "/cookies", label: "Cookie Policy" },
  { to: "/child-safety", label: "Child Safety & Safeguarding" },
  { to: "/responsible-ai", label: "Responsible AI" },
  { to: "/mentor-code", label: "Mentor Code of Conduct" },
];

const MD: Components = {
  h1: (p) => <h1 className="mb-4 font-display text-3xl font-light tracking-tight md:text-4xl" {...p} />,
  h2: (p) => <h2 className="mt-6 mb-2 border-t border-border pt-4 font-display text-xl font-semibold" {...p} />,
  h3: (p) => <h3 className="mt-4 mb-1.5 font-display text-lg font-semibold" {...p} />,
  p: (p) => <p className="my-2 text-[15px] leading-6 text-foreground/85" {...p} />,
  ul: (p) => <ul className="my-2 list-disc space-y-1 pl-5 text-[15px] leading-6 text-foreground/85 marker:text-primary" {...p} />,
  ol: (p) => <ol className="my-2 list-decimal space-y-1 pl-5 text-[15px] leading-6 text-foreground/85" {...p} />,
  a: ({ href = "", ...p }) =>
    href.startsWith("/") ? (
      <Link to={href} className="text-primary underline-offset-4 hover:underline" {...p} />
    ) : (
      <a href={href} className="text-primary underline-offset-4 hover:underline" {...p} />
    ),
  strong: (p) => <strong className="font-semibold text-foreground" {...p} />,
  em: (p) => <em className="text-muted-foreground" {...p} />,
  table: (p) => (
    <div className="my-3 overflow-x-auto border border-border">
      <table className="w-full border-collapse text-sm" {...p} />
    </div>
  ),
  th: (p) => <th className="bg-muted px-3 py-2 text-left font-semibold" {...p} />,
  td: (p) => <td className="border-t border-border px-3 py-2 align-top text-foreground/85" {...p} />,
};

interface LegalLayoutProps {
  markdown: string;
  banner?: ReactNode;
}

/**
 * Policy shell, IBM-style: dark top bar, white reading surface,
 * sticky list of every policy on the left, generous body type.
 */
export default function LegalLayout({ markdown, banner }: LegalLayoutProps) {
  return (
    <div className="theme-reading min-h-screen bg-background text-foreground flex flex-col">
      <header className="sticky top-0 z-50 bg-foreground text-background">
        <div className="mx-auto flex h-12 max-w-7xl items-center justify-between px-4 sm:px-6">
          <Link to="/" className="flex items-center gap-2 text-sm font-semibold">
            <WeKitLogo size="sm" />
            WeKIT™ Policies
          </Link>
          <Link to="/" className="inline-flex min-h-11 items-center gap-1.5 text-sm opacity-80 hover:opacity-100">
            <ArrowLeft className="h-4 w-4" aria-hidden="true" /> Back to home
          </Link>
        </div>
      </header>

      <main className="flex-1">
        <div className="mx-auto grid max-w-7xl gap-6 px-4 py-6 sm:px-6 md:py-10 lg:grid-cols-[240px_1fr]">
          <nav aria-label="Policies" className="lg:sticky lg:top-20 lg:self-start">
            <p className="mb-3 text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">Policies</p>
            <ul className="flex gap-2 overflow-x-auto pb-2 lg:flex-col lg:gap-0 lg:border-l lg:border-border lg:pb-0">
              {POLICIES.map((p) => (
                <li key={p.to} className="shrink-0">
                  <NavLink
                    to={p.to}
                    className={({ isActive }) =>
                      `block min-h-11 whitespace-nowrap rounded-full border px-4 py-2.5 text-sm lg:-ml-px lg:rounded-none lg:border-0 lg:border-l-2 lg:py-2 ${
                        isActive
                          ? "border-primary font-semibold text-foreground lg:border-primary"
                          : "border-border text-muted-foreground hover:text-foreground lg:border-transparent"
                      }`
                    }
                  >
                    {p.label}
                  </NavLink>
                </li>
              ))}
              <li className="shrink-0">
                <button
                  type="button"
                  onClick={openCookiePreferences}
                  className="block min-h-11 whitespace-nowrap rounded-full border border-border px-4 py-2.5 text-left text-sm text-primary hover:underline lg:-ml-px lg:rounded-none lg:border-0 lg:border-l-2 lg:border-transparent lg:py-2"
                >
                  Cookie preferences
                </button>
              </li>
            </ul>
          </nav>

          <div className="min-w-0 max-w-3xl">
            {banner}
            <article className="text-foreground">
              <ReactMarkdown remarkPlugins={[remarkGfm]} components={MD}>{markdown}</ReactMarkdown>
            </article>
            <PolicyAgree />
          </div>
        </div>
      </main>

      <SharedFooter />
    </div>
  );
}
